import {
	Box,
	Button,
	Chip,
	Grid,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import {BreadcrumbItem, BreadcrumbLink, Breadcrumbs, ErrorAlert, View, ViewHeader} from "@variocube/app-ui";
import React, {FormEvent} from "react";
import {useAsyncCallback} from "react-async-hook";
import {Link as RouterLink} from "react-router-dom";
import {FilePreview} from "../../../../component/file-preview";
import {useLocalization} from "../../../../i18n";
import {InvoiceFile} from "../types";

interface UploadInvoiceSummaryViewProps {
	invoices: InvoiceFile[];
	onBack: () => any;
	onSubmit: () => Promise<any>;
}

export function UploadInvoiceSummaryView({invoices, onBack, onSubmit}: Readonly<UploadInvoiceSummaryViewProps>) {
	const {t} = useLocalization();

	const submit = useAsyncCallback(async (e: FormEvent) => {
		e.preventDefault();
		await onSubmit();
	});

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={RouterLink} to="/invoices">{t("invoices.plural")}</BreadcrumbLink>
				<BreadcrumbItem>{t("invoices.new.creditor.title")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader
				title={t("invoices.new.creditor.title")}
				subTitle={t("invoices.new.creditor.submit")}
			/>
			<Paper>
				<TableContainer>
					<Table sx={{tableLayout: "fixed"}}>
						<TableHead>
							<TableRow>
								<TableCell width={260}>{t("invoices.singular")}</TableCell>
								<TableCell width={180}>{t("invoices.recipient")}</TableCell>
								<TableCell width={140}>
									{t("invoices.details.orderNumbers")} / {t("invoices.details.costCenter")}
								</TableCell>
								<TableCell>{t("invoices.details.additionalInformation")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{invoices.map(invoice => (
								<TableRow key={invoice.checksum}>
									<TableCell width={240}>
										<FilePreview file={invoice.file} />
									</TableCell>
									<TableCell>
										<Box>{invoice.inbox?.name}</Box>
										<Box mt={2}>{invoice.contactOebb}</Box>
									</TableCell>
									<TableCell>
										<Stack spacing={1}>
											{invoice.orderNumbers.map(orderNumber => (
												<Chip key={orderNumber} label={orderNumber} />
											))}
											{invoice.profitCenter && (
												<Chip key={invoice.profitCenter} label={invoice.profitCenter} />
											)}
										</Stack>
									</TableCell>
									<TableCell>
										<Typography sx={{textWrap: "pre-wrap", wordBreak: "break-word"}}>
											{invoice.additionalInfo}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			{submit.error && <ErrorAlert error={submit.error} />}
			<form onSubmit={submit.execute}>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<Button color="inherit" variant="outlined" fullWidth onClick={onBack} disabled={submit.loading}>
							{t("actions.back")}
						</Button>
					</Grid>
					<Grid item xs={6}>
						<Button type="submit" variant="contained" fullWidth disabled={submit.loading}>
							{t("invoices.new.creditor.title")}
						</Button>
					</Grid>
				</Grid>
			</form>
		</View>
	);
}
